import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Target, ArrowLeft, LogOut, UserCog, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const ADMIN_EMAIL = "johan.gotvall@gmail.com";

/**
 * Shared header used across most pages.
 * variant: "home" (no back), "back" (back arrow)
 * hideOnScrollDown: when true, header slides out of view when scrolling down
 *                   and slides back in when scrolling up.
 */
export default function AppHeader({
  subtitle,
  backTo,
  backLabel = "Tillbaka",
  right,
  hideOnScrollDown = false,
}) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!hideOnScrollDown) return undefined;
    let lastY = window.scrollY;
    let lockUntil = 0;
    const onForceHide = () => {
      lockUntil = Date.now() + 1200;
      setHidden(true);
    };
    const onScroll = () => {
      const y = window.scrollY;
      if (Date.now() < lockUntil) {
        lastY = y;
        return;
      }
      // Always show near the top of the page
      if (y < 80) {
        setHidden(false);
      } else if (y > lastY + 6) {
        setHidden(true);
      } else if (y < lastY - 6) {
        setHidden(false);
      }
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("appheader-hide", onForceHide);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("appheader-hide", onForceHide);
    };
  }, [hideOnScrollDown]);

  const handleLogout = () => {
    logout();
    navigate("/gate");
  };
  return (
    <header
      className="border-b border-[#27272A] bg-[#0A0A0A]/80 backdrop-blur-md sticky top-0 z-30 transition-transform duration-300"
      style={hidden ? { transform: "translateY(-100%)" } : undefined}
      data-testid="app-header"
      data-hidden={hidden ? "true" : "false"}
    >
      <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center gap-4 flex-wrap">
        <Link to="/" className="flex items-center gap-3" data-testid="header-home-link">
          <div className="w-10 h-10 border-2 border-[#34C759] flex items-center justify-center">
            <Target size={20} className="text-[#34C759]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-black text-xl uppercase tracking-widest leading-none">
              Målvaktslabb
            </div>
            <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mt-1">
              {subtitle || "Handboll · Statistik"}
            </div>
          </div>
        </Link>

        {backTo !== undefined && (
          <Button
            data-testid="header-back-btn"
            onClick={() => navigate(backTo)}
            variant="outline"
            className="h-10 rounded-none border-[#27272A] bg-transparent hover:bg-[#171717] text-white ml-4"
          >
            <ArrowLeft size={16} className="mr-2" />
            {backLabel}
          </Button>
        )}

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {right}
          {user && (
            <div className="flex items-center gap-2 pl-2 ml-1 border-l border-[#27272A]">
              <span
                className="text-[11px] text-zinc-500 uppercase tracking-widest font-display hidden md:block"
                data-testid="header-user-email"
                title={user.email}
              >
                {user.email}
              </span>
              {user.email?.toLowerCase() === ADMIN_EMAIL && (
                <Button
                  data-testid="header-admin-btn"
                  onClick={() => navigate("/admin")}
                  variant="outline"
                  className="h-10 rounded-none border-[#34C759] bg-transparent hover:bg-[#34C759]/10 text-[#34C759]"
                  title="Admin"
                >
                  <ShieldCheck size={14} />
                </Button>
              )}
              <Button
                data-testid="header-profile-btn"
                onClick={() => navigate("/profile")}
                variant="outline"
                className="h-10 rounded-none border-[#27272A] bg-transparent hover:bg-[#171717] text-white"
                title="Profil"
              >
                <UserCog size={14} />
              </Button>
              <Button
                data-testid="header-logout-btn"
                onClick={handleLogout}
                variant="outline"
                className="h-10 rounded-none border-[#27272A] bg-transparent hover:bg-[#171717] text-white"
                title="Logga ut"
              >
                <LogOut size={14} />
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
